import { DeviceClasses } from '@racemap/utilities/consts/events';
import type { Immutable } from 'immer';
import type { FC } from 'react';
import type { CurrentEvent } from 'src/store/events/events_reducers';
import type { CheckpointEntry } from '../../BasicComponents/CheckList';
import { ParticipantListLink } from '../../BasicComponents/Links';
import { Checkpoint } from './Checkpoint';

interface Props {
  event: Immutable<CurrentEvent> | null;
}

export const TransponderCheckpoint: FC<Props> = ({ event }) => {
  if (event == null) return null;

  if (event.starters == null || event.starters.size === 0) {
    return (
      <Checkpoint title="Transponders">
        You have to add particants with transponder ids to the{' '}
        <ParticipantListLink eventId={event.id} />.
      </Checkpoint>
    );
  }

  const transponders = Array.from(event.starters.values()).filter(
    (starter) =>
      starter.deviceClass === DeviceClasses.Transponder ||
      starter.deviceClass === DeviceClasses.MyLapsTransponder ||
      starter.deviceClass === DeviceClasses.FeibotTransponder ||
      starter.deviceClass === DeviceClasses.TrackPingTransponder ||
      starter.deviceClass === DeviceClasses.ChronoTrackTransponder,
  );
  const transponderCount = transponders.length;

  if (event.starters.size - transponderCount > 0) {
    return (
      <Checkpoint title="Transponders">
        You have imported {transponderCount} transponder(s) to the{' '}
        <ParticipantListLink eventId={event.id} />. {event.starters.size - transponderCount}{' '}
        participant(s) are not assigned to a transponder. These participants are not considered in
        the prediction.
      </Checkpoint>
    );
  }

  if (transponderCount > 0) {
    return (
      <Checkpoint title="Transponders">
        You have imported {transponderCount} transponder(s) to the{' '}
        <ParticipantListLink eventId={event.id} />. Each participant has been assigned a transponder
        id.
      </Checkpoint>
    );
  }

  return (
    <Checkpoint title="Transponders">
      There are no transponders in this tracking map. You have to import participants with
      transponder ids to the <ParticipantListLink eventId={event.id} />.
    </Checkpoint>
  );
};

export const getTransponderCheckpointState = (
  event: Immutable<CurrentEvent> | null,
): CheckpointEntry['status'] => {
  if (event == null) return 'secondary';
  if (event.starters == null || event.starters.size === 0) return 'danger';

  const transponders = Array.from(event.starters.values()).filter(
    (starter) =>
      starter.deviceClass === DeviceClasses.Transponder ||
      starter.deviceClass === DeviceClasses.FeibotTransponder ||
      starter.deviceClass === DeviceClasses.MyLapsTransponder ||
      starter.deviceClass === DeviceClasses.TrackPingTransponder ||
      starter.deviceClass === DeviceClasses.ChronoTrackTransponder,
  );
  const transponderCount = transponders.length;

  if (event.starters.size - transponderCount > 0) {
    return 'warning';
  }

  if (transponderCount > 0) {
    return 'success';
  }

  return 'danger';
};
